import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { motion, useInView, useReducedMotion } from "framer-motion";
import type { StoryStyleId } from "../constants/storyStyles";
import { HOME_SECTION_BACKGROUND_URL } from "../constants/homeSectionBackground";
import { STORY_STYLE_IDS, storyStylePreviewUrl } from "../constants/storyStyles";
import { useTranslation } from "../i18n/useTranslation";

const STORY_COUNT = STORY_STYLE_IDS.length;

function wrapIndex(i: number): number {
  return ((i % STORY_COUNT) + STORY_COUNT) % STORY_COUNT;
}

/** Carousel preview (center + sides): strong bright flash, then easing into the new artwork */
const STYLE_PREVIEW_FLASH_EASE: [number, number, number, number] = [0.18, 0.92, 0.38, 1];
const STYLE_PREVIEW_FLASH_SECONDS = 5;

const headingTitleClass =
  "mb-10 bg-gradient-to-r from-brandNavy via-brandOrange to-brandBlue bg-clip-text px-1 text-[1.625rem] font-extrabold leading-tight text-transparent sm:mb-12 sm:text-3xl sm:px-0 md:text-4xl";

const HomeDifferentStyles: React.FC = () => {
  const { t } = useTranslation();
  const prefersReducedMotion = useReducedMotion();
  const sectionRef = useRef<HTMLElement>(null);
  const headingInView = useInView(sectionRef, { once: true, margin: "-80px" });
  const [activeIndex, setActiveIndex] = useState(0);
  const [previewOpen, setPreviewOpen] = useState(false);
  const previewCloseRef = useRef<HTMLButtonElement>(null);
  const touchStartX = useRef<number | null>(null);
  const previewTitleId = `home-style-preview-${useId().replace(/:/g, "")}`;

  const goPrev = useCallback(() => {
    setActiveIndex((i) => wrapIndex(i - 1));
  }, []);

  const goNext = useCallback(() => {
    setActiveIndex((i) => wrapIndex(i + 1));
  }, []);

  const leftIdx = wrapIndex(activeIndex - 1);
  const centerIdx = activeIndex;
  const rightIdx = wrapIndex(activeIndex + 1);

  const onTouchStart = (e: React.TouchEvent) => {
    touchStartX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    if (touchStartX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    touchStartX.current = null;
    if (dx > 56) goPrev();
    else if (dx < -56) goNext();
  };

  useEffect(() => {
    if (!previewOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prevOverflow;
    };
  }, [previewOpen]);

  useEffect(() => {
    if (!previewOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setPreviewOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [previewOpen]);

  useEffect(() => {
    if (!previewOpen) return;
    const id = window.requestAnimationFrame(() => previewCloseRef.current?.focus());
    return () => window.cancelAnimationFrame(id);
  }, [previewOpen]);

  const renderCard = (
    slotKey: "left" | "center" | "right",
    id: StoryStyleId,
    variant: "side" | "center",
    openPreview?: () => void,
  ) => {
    const titleKey = `wizard.storyStyle.${id}Title` as const;
    const isCenter = variant === "center";

    const imageInner = (
      <img
        src={storyStylePreviewUrl(id)}
        alt=""
        width={640}
        height={480}
        className="h-full w-full object-cover"
        loading={isCenter ? "eager" : "lazy"}
        decoding="async"
      />
    );

    const motionFlashLayers = prefersReducedMotion ? null : (
      <>
        <motion.div
          key={id}
          className="absolute inset-0"
          initial={{ filter: "brightness(2.45)" }}
          animate={{ filter: "brightness(1)" }}
          transition={{ duration: STYLE_PREVIEW_FLASH_SECONDS, ease: STYLE_PREVIEW_FLASH_EASE }}
          style={{ willChange: "filter" }}
        >
          {imageInner}
        </motion.div>
        <motion.div
          key={`${id}-bleach`}
          className="pointer-events-none absolute inset-0 z-[1] bg-white"
          aria-hidden
          initial={{ opacity: 0.8 }}
          animate={{ opacity: 0 }}
          transition={{ duration: STYLE_PREVIEW_FLASH_SECONDS, ease: STYLE_PREVIEW_FLASH_EASE }}
        />
      </>
    );

    const articleClass = [
      "shrink-0",
      isCenter
        ? // No percentage widths — avoids reflow when neighbors / titles change between slides.
          "z-10 w-[16.5rem] min-[390px]:w-[17.25rem] sm:w-64 md:w-[17.5rem]"
        : "w-[9.375rem] min-[390px]:w-[10rem] opacity-[0.88] sm:w-44 md:w-52",
    ].join(" ");

    const inner = (
      <div
        className={[
          "overflow-hidden rounded-2xl border border-white/30 bg-white/60 shadow-card backdrop-blur-sm",
          isCenter ? "shadow-lg" : "shadow-card",
        ].join(" ")}
      >
        {isCenter && openPreview ? (
          prefersReducedMotion ? (
            <button
              type="button"
              onClick={openPreview}
              className="group relative flex aspect-[4/3] w-full cursor-zoom-in items-stretch overflow-hidden bg-slate-100 p-0 text-left transition hover:brightness-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-brandOrange focus-visible:ring-offset-2 focus-visible:ring-offset-brandBeige"
              aria-label={`${t("home.differentStylesExpandPreviewAria")}: ${t(titleKey)}`}
            >
              <span className="absolute inset-0 block overflow-hidden">{imageInner}</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={openPreview}
              className="group relative aspect-[4/3] w-full cursor-zoom-in overflow-hidden bg-slate-100 p-0 text-left transition hover:brightness-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-brandOrange focus-visible:ring-offset-2 focus-visible:ring-offset-brandBeige"
              aria-label={`${t("home.differentStylesExpandPreviewAria")}: ${t(titleKey)}`}
            >
              {motionFlashLayers}
            </button>
          )
        ) : isCenter ? (
          prefersReducedMotion ? (
            <div className="aspect-[4/3] w-full overflow-hidden bg-slate-100">{imageInner}</div>
          ) : (
            <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-100">{motionFlashLayers}</div>
          )
        ) : prefersReducedMotion ? (
          <div className="aspect-[4/3] w-full overflow-hidden bg-slate-100">{imageInner}</div>
        ) : (
          <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-100">{motionFlashLayers}</div>
        )}
        <h3
          className={[
            "break-words border-t border-brandNavy/8 px-2 py-2.5 text-center font-bold leading-snug text-brandNavy sm:px-4 sm:py-3",
            "line-clamp-2",
            isCenter ? "min-h-[3rem] text-sm sm:min-h-[3.25rem] sm:text-base" : "min-h-[2.75rem] text-xs sm:min-h-[3rem] sm:text-sm",
          ].join(" ")}
        >
          {t(titleKey)}
        </h3>
      </div>
    );

    return (
      <article key={slotKey} className={articleClass} aria-hidden={!isCenter}>
        {inner}
      </article>
    );
  };

  const centerId = STORY_STYLE_IDS[centerIdx];
  const centerTitleKey = `wizard.storyStyle.${centerId}Title` as const;

  return (
    <section
      ref={sectionRef}
      id="different-styles"
      className="relative z-10 scroll-mt-20 overflow-hidden bg-brandBeige px-4 py-11 text-center sm:px-6 sm:py-16"
      aria-labelledby="home-different-styles-heading"
    >
      <img
        src={HOME_SECTION_BACKGROUND_URL}
        alt=""
        className="pointer-events-none absolute inset-0 h-full w-full object-cover object-center"
        aria-hidden
      />
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-b from-white/35 via-white/10 to-brandBeige/25"
        aria-hidden
      />
      <div className="relative z-10 mx-auto max-w-6xl">
        <motion.h2
          id="home-different-styles-heading"
          className={headingTitleClass}
          initial={prefersReducedMotion ? false : { opacity: 0, y: 12 }}
          animate={
            headingInView ? { opacity: 1, y: 0 } : prefersReducedMotion ? {} : { opacity: 0, y: 12 }
          }
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          {t("home.differentStylesTitle")}
        </motion.h2>

        <div className="relative">
          <p className="sr-only" aria-live="polite">
            {t(centerTitleKey)}
          </p>
          <div
            role="group"
            aria-label={t("home.differentStylesCarouselAria")}
            onTouchStart={onTouchStart}
            onTouchEnd={onTouchEnd}
            className="flex items-start justify-center gap-2 overflow-hidden py-2 sm:gap-4 md:gap-6 [-webkit-tap-highlight-color:transparent]"
          >
            {renderCard("left", STORY_STYLE_IDS[leftIdx], "side")}
            <div className="flex w-[16.5rem] min-[390px]:w-[17.25rem] shrink-0 flex-col items-center gap-3 sm:w-64 sm:gap-4 md:w-[17.5rem]">
              {renderCard("center", STORY_STYLE_IDS[centerIdx], "center", () => setPreviewOpen(true))}
              <div className="flex justify-center gap-2">
                <button
                  type="button"
                  onClick={goPrev}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-brandNavy/15 bg-white/90 text-brandNavy shadow-soft transition hover:border-brandOrange/40 hover:bg-white focus:outline-none focus:ring-2 focus:ring-headerCta focus:ring-offset-2"
                  aria-label={t("home.differentStylesScrollPrevAria")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                    <path d="M15 18l-6-6 6-6" />
                  </svg>
                </button>
                <button
                  type="button"
                  onClick={goNext}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-brandNavy/15 bg-white/90 text-brandNavy shadow-soft transition hover:border-brandOrange/40 hover:bg-white focus:outline-none focus:ring-2 focus:ring-headerCta focus:ring-offset-2"
                  aria-label={t("home.differentStylesScrollNextAria")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              </div>
            </div>
            {renderCard("right", STORY_STYLE_IDS[rightIdx], "side")}
          </div>
        </div>
      </div>

      {typeof document !== "undefined" &&
        previewOpen &&
        createPortal(
          <div className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-6" role="presentation">
            <div
              className="absolute inset-0 bg-brandNavy/55 backdrop-blur-[2px]"
              aria-hidden
              onClick={() => setPreviewOpen(false)}
            />
            <div
              role="dialog"
              aria-modal="true"
              aria-labelledby={previewTitleId}
              className="relative z-[1] max-h-[min(92vh,52rem)] w-full max-w-4xl overflow-hidden rounded-2xl border border-white/25 bg-brandBeige/95 shadow-2xl"
            >
              <div className="flex items-start justify-between gap-2 border-b border-brandNavy/10 px-3 py-2 sm:px-4">
                <p id={previewTitleId} className="min-w-0 flex-1 pt-1 text-sm font-bold text-brandNavy sm:text-base">
                  {t(centerTitleKey)}
                </p>
                <button
                  ref={previewCloseRef}
                  type="button"
                  onClick={() => setPreviewOpen(false)}
                  className="shrink-0 rounded-full border border-brandNavy/15 bg-white/95 p-2 text-brandNavy shadow-soft transition hover:border-brandOrange/40 focus:outline-none focus:ring-2 focus:ring-headerCta focus:ring-offset-2"
                  aria-label={t("home.differentStylesClosePreviewAria")}
                >
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden>
                    <path d="M18 6 6 18M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="max-h-[calc(92vh-4rem)] overflow-auto bg-slate-900/5 p-2 sm:p-4">
                <img
                  src={storyStylePreviewUrl(centerId)}
                  alt={t(centerTitleKey)}
                  width={1280}
                  height={960}
                  className="mx-auto h-auto max-h-[min(78vh,44rem)] w-full max-w-full object-contain"
                  decoding="async"
                />
              </div>
            </div>
          </div>,
          document.body,
        )}
    </section>
  );
};

export default HomeDifferentStyles;
